import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { message as antdMessage } from 'antd';
import { STORAGE_KEYS, env } from '@/shared/config';
import { storage } from '@/shared/lib/utils';
import { useAuthStore } from '@/shared/lib/store/useAuthStore';
import { useAppStore } from '@/shared/lib/store/useAppStore';
import type { ApiResponse, ApiResponseList } from './types';

// Base URL - loaded from environment config (single source of truth)
// Version prefix is specified per endpoint (e.g., /v1/admin/auth/login)

/** HTTP 200 + success:false — session yo'q yoki tugagan. */
const UNAUTHORIZED_ERROR_CODE = 1005;

type ApiPayload = Partial<ApiResponse> | Partial<ApiResponseList>;

let isRedirectingToLogin = false;

const getResultErrorCode = (payload: ApiPayload | undefined): number | null => {
  const result = payload?.result;
  if (!result || typeof result !== 'object' || !('errorCode' in result)) return null;

  const code = Number((result as { errorCode?: unknown }).errorCode);
  return Number.isFinite(code) ? code : null;
};

const isUnauthorizedPayload = (payload: ApiPayload | undefined): boolean =>
  Boolean(payload && typeof payload === 'object' && 'success' in payload && payload.success !== true) &&
  getResultErrorCode(payload) === UNAUTHORIZED_ERROR_CODE;

const redirectToLogin = async () => {
  if (window.location.pathname === '/login' || isRedirectingToLogin) return;
  isRedirectingToLogin = true;

  const { queryClient } = await import('./queryClient');
  queryClient.clear();
  useAuthStore.getState().logout();
  window.location.assign('/login');
};

// Create axios instance
export const axiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - attach token to every request
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = storage.get(STORAGE_KEYS.ACCESS_TOKEN);
    const language = useAppStore.getState().language;

    if (config.headers) {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Send current UI language to backend
      config.headers['Accept-Language'] = language;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    const payload = response?.data as ApiPayload | undefined;

    // Server ba'zan 200 qaytaradi, lekin sessiya yaroqsiz (errorCode 1005).
    if (isUnauthorizedPayload(payload)) {
      void redirectToLogin();
      return Promise.reject(
        new axios.AxiosError(
          (payload?.result as { message?: string } | null)?.message ?? 'Avtorizatsiyadan o\'tmagansiz.',
          'ERR_UNAUTHORIZED',
          response.config,
          response.request,
          response
        )
      );
    }

    if (payload && typeof payload === 'object' && 'success' in payload) {
      const isSuccess = payload.success === true;
      if (!isSuccess) {
        const result = payload.result as Record<string, unknown> | null | undefined;
        const errMsg =
          (payload as ApiResponse).error?.message ??
          (result && typeof result === 'object' ? (result.message as string | undefined) : undefined) ??
          'Xato';
        antdMessage.error(errMsg);
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // 401 Unauthorized - token expired
    if (error.response?.status === 401 && originalRequest) {
      await redirectToLogin();
    }

    return Promise.reject(error);
  }
);
